import "./Transactions.css";
import { useEffect, useState } from "react";
import api from "../../../../api/resources";
import { Content, Header } from "../../../commons/Template/Template";
import { FormLoader } from "../../../commons/Form/Form";
import { EmptyList } from "../../../Financing/Loans/Loans";
import {
  Table,
  TableBody,
  TableHeader,
  TableRow,
  TableWrapper,
} from "../../../commons/Table/Table";
import { formatAmount, formatDate } from "../../../../format";
import { i18n } from "../../../../i18n";
import { Input } from "../../../commons/Form/Form";
import Button from "../../../commons/Button/Button";
import {
  Download,
  FirstPage,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  LastPage,
} from "@mui/icons-material";
import { t } from "i18next";
import externalRoutes from "../../../../routes/externalRoutes";
import Typo from "../../../commons/Typo/Typo";

export default function Transactions({ accountID, onBack }) {
  const [loading, setLoading] = useState(true);

  // Accounts
  const [accounts, setAccounts] = useState(null);

  // Transactions
  const [transactions, setTransactions] = useState(null);
  const [pages, setPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const initialQueryParams = {
    account: "",
    min_date: "",
    max_date: "",
    min_amount: "",
    max_amount: "",
    text: "",
  };

  const [queryParams, setQueryParams] = useState({
    ...initialQueryParams,
    account: accountID,
  });

  const handleQueryParamsChange = (field, value) => {
    setQueryParams((prevParams) => ({
      ...prevParams,
      [field]: value,
    }));
  };

  const computePages = (totalCount, firstPageCount) => {
    let nbPages = 1;
    if (totalCount > firstPageCount) {
      nbPages =
        Math.floor(totalCount / firstPageCount) +
        (totalCount % firstPageCount && 1);
    }

    let pagesArray = [];
    for (let i = 1; i <= nbPages; i++) {
      pagesArray.push(i);
    }

    return pagesArray;
  };

  const initTransactions = async () => {
    setCurrentPage(1);
    const response = await api.getTransactions(queryParams);
    if (response.ok) {
      const jsonResponse = await response.json();
      setTransactions(jsonResponse.results);
      // Compute number of pages only when there's data and only on first page (`previous = null`),
      // because `firstPageCount` is the reference
      if (jsonResponse.count > 0 && !jsonResponse.previous) {
        setPages(computePages(jsonResponse.count, jsonResponse.results.length));
      }
    }
    return response.ok;
  };

  const initAccounts = async () => {
    const response = await api.getAccounts();
    if (response.ok) {
      const jsonResponse = await response.json();
      setAccounts(jsonResponse);
    }
    return response.ok;
  };

  const init = async () => {
    setLoading(true);
    const ok = (await initTransactions()) && (await initAccounts());
    ok && setLoading(false);
  };

  useEffect(() => {
    init();
  }, []);

  const handlePageChange = async () => {
    setLoading(true);
    const response = await api.getTransactions({
      ...queryParams,
      page: currentPage,
    });
    if (response.ok) {
      const jsonResponse = await response.json();
      setTransactions(jsonResponse.results);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentPage && transactions) {
      handlePageChange();
    }
  }, [currentPage]);

  const handleFilter = async (queryParamsObj) => {
    setLoading(true);
    setCurrentPage(1);
    setPages(null);

    const response = await api.getTransactions(queryParamsObj);
    if (response.ok) {
      const jsonResponse = await response.json();
      setTransactions(jsonResponse.results);
      if (jsonResponse.count > 0 && !jsonResponse.previous) {
        setPages(computePages(jsonResponse.count, jsonResponse.results.length));
      }
      setLoading(false);
    }
  };

  const handleClearQueryParams = () => {
    setQueryParams(initialQueryParams);
    handleFilter(initialQueryParams);
  };

  const handleExport = async () => {
    const url = api.getTransactionsFile(
      externalRoutes.Banking.downloadTransactions,
      { ...queryParams, page: "" },
    );
    window.location = url;
  };

  const formatAmountFn = (amount, currency) =>
    amount ? formatAmount(amount, i18n.resolvedLanguage, currency) : "-";

  const thStyle = {
    textTransform: "unset",
    backgroundColor: "var(--color-leano-1)",
    color: "white",
    position: "sticky",
    top: "5.75rem", // height of menu including company switch banner
  };

  if (loading) return <FormLoader />;
  return (
    <Content
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        position: "relative",
      }}
    >
      <Header onBackButtonClick={onBack}>
        <Typo variant="h2">{t("vault:tabs.bank.transactions.heading")}</Typo>
        <Typo variant="body2">
          {t("vault:tabs.bank.transactions.subheading")}
        </Typo>
      </Header>
      <div className="Transactions-filters">
        <div className="Transactions-filters-group">
          <DateFilter
            label={t("common:min_date")}
            date={queryParams.min_date}
            onChange={(value) => handleQueryParamsChange("min_date", value)}
          />

          <DateFilter
            label={t("common:max_date")}
            date={queryParams.max_date}
            onChange={(value) => handleQueryParamsChange("max_date", value)}
          />

          <AmountFilter
            label={t("common:min_amount")}
            amount={queryParams.min_amount}
            onChange={(value) => handleQueryParamsChange("min_amount", value)}
          />
          <AmountFilter
            label={t("common:max_amount")}
            amount={queryParams.max_amount}
            onChange={(value) => handleQueryParamsChange("max_amount", value)}
          />
          <AccountFilter
            label={t("common:account")}
            availableAccounts={accounts}
            accountID={queryParams.account}
            onChange={(value) => handleQueryParamsChange("account", value)}
          />
        </div>
        <div style={{ flexGrow: "1" }}>
          <WordingFilter
            label={t("common:label")}
            wordingText={queryParams.text}
            onChange={(value) => handleQueryParamsChange("text", value)}
          />
        </div>

        <div className="Transactions-filters-buttons">
          <Button
            variant={"secondary_b"}
            text={t("common:reset")}
            onClick={handleClearQueryParams}
          />
          <Button
            variant={"primary_a"}
            text={t("common:search")}
            disabled={Object.values(queryParams).every((value) => !value)}
            onClick={() => handleFilter({ ...queryParams, page: "" })}
          />
          <Button
            variant={"primary_a"}
            text={t("common:exportToXlsx")}
            icon={<Download />}
            onClick={handleExport}
          />
        </div>
      </div>
      {transactions && transactions.length ? (
        <TableWrapper
          style={{
            position: "relative",
            overflow: "unset",
            gap: "1rem",
            border: "unset",
          }}
        >
          <Table nested fixedLayout style={{ position: "relative" }}>
            <TableHeader>
              <TableRow>
                <th style={{ ...thStyle, width: "50%", minWidth: "10rem" }}>
                  {t("common:label")}
                </th>
                <th style={thStyle}>{t("common:account")}</th>
                <th style={{ ...thStyle, textAlign: "right" }}>
                  {t("common:amount")}
                </th>
                <th style={{ ...thStyle, textAlign: "right" }}>
                  {t("common:date")}
                </th>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.map((transaction, transactionIndex) => (
                <TableRow key={transactionIndex}>
                  <td title={transaction.wording}>{transaction.wording}</td>
                  <td
                    title={
                      accounts.find(
                        (account) => account.id === transaction.account,
                      ).name
                    }
                  >
                    {
                      accounts.find(
                        (account) => account.id === transaction.account,
                      ).name
                    }
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {formatAmountFn(
                      transaction.value,
                      accounts.find(
                        (account) => account.id === transaction.account,
                      ).currency,
                    )}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {formatDate(
                      transaction.date,
                      i18n.resolvedLanguage,
                      "shortMonth",
                    )}
                  </td>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div style={{ marginBottom: "7rem" }}>
            {pages && pages.length > 0 && (
              <PageSwitcher
                pages={pages}
                currentPage={currentPage}
                onCurrentPageChange={setCurrentPage}
              />
            )}
          </div>
        </TableWrapper>
      ) : (
        <EmptyList />
      )}
    </Content>
  );
}

function PageSwitcher({ pages, currentPage, onCurrentPageChange }) {
  const [page, setPage] = useState({ label: currentPage, value: currentPage });

  const handleChange = (newOption) => {
    setPage(newOption);
    onCurrentPageChange(newOption.value);
  };

  const currentPageInput = {
    type: "select",
    name: "currentPage",
    value: page,
    onChange: (value) => handleChange(value),
    options: pages.map((page) => ({
      label: page,
      value: page,
    })),
    autoSortOptionsDisabled: true,
  };

  return (
    <div
      className="PageSwitcher"
      style={{
        display: "flex",
        gap: "2rem",
        alignItems: "center",
        // marginBottom: "15rem",
      }}
    >
      <Input {...currentPageInput} className={"PageSwitcher-SelectInput"} />
      <Button
        icon={<FirstPage />}
        onClick={() => handleChange({ label: 1, value: 1 })}
      />
      <Button
        icon={<KeyboardArrowLeft />}
        onClick={() => {
          if (currentPage > 1) {
            handleChange({ label: currentPage - 1, value: currentPage - 1 });
          }
        }}
      />
      <Button
        icon={<KeyboardArrowRight />}
        onClick={() => {
          if (currentPage < pages.length) {
            handleChange({ label: currentPage + 1, value: currentPage + 1 });
          }
        }}
      />
      <Button
        icon={<LastPage />}
        onClick={() =>
          handleChange({ label: pages.length, value: pages.length })
        }
      />
    </div>
  );
}

function DateFilter({ label, date, onChange }) {
  const input = {
    type: "date",
    name: label,
    label: label,
    value: date,
    onChange: (value) => onChange(value),
  };

  return <Input {...input} />;
}

function AmountFilter({ label, amount, onChange }) {
  const input = {
    type: "number",
    name: label,
    label: label,
    value: amount,
    onChange: (value) => onChange(value),
  };

  return <Input {...input} />;
}

function AccountFilter({ label, availableAccounts, onChange, accountID }) {
  const options = availableAccounts.map((availableAccount) => ({
    label: availableAccount.name,
    value: availableAccount.id,
  }));

  const [accountObj, setAccountObj] = useState(
    accountID
      ? options.find((accountObj) => accountObj.value === accountID)
      : null,
  );

  const handleChange = (value) => {
    setAccountObj(value);
    onChange(value ? value.value : "");
  };

  const input = {
    type: "select",
    label: label,
    value: accountObj,
    onChange: (value) => handleChange(value),
    options: options,
    isClearable: true,
  };

  return <Input className={"ListFilter"} {...input} />;
}

function WordingFilter({ label, wordingText, onChange }) {
  const input = {
    type: "text",
    name: label,
    placeholder: label,
    value: wordingText,
    onChange: (value) => onChange(value),
  };

  return <Input className="ListFilter" {...input} />;
}
